import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { traxUserListSchema } from "~/shared/schemas/trax-user-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

// Prefix key used for invalidating ALL customer-scoped vendor user caches
// (e.g. from add/update/delete mutations). TanStack Query matches by prefix
// by default, so invalidating this key invalidates every per-customer entry.
const vendorUsersQueryKey = ["vendorUsers"] as const;

const getVendorUsersQueryKey = (customerId: number | undefined) =>
	[...vendorUsersQueryKey, customerId ?? null] as const;

export { getVendorUsersQueryKey, vendorUsersQueryKey };

const getVendorUsers = async (route: string, signal: AbortSignal) => {
	const resp = await getAndValidate(route, traxUserListSchema, { signal });
	if (!resp.success) {
		throw new Error(resp.error);
	}
	return resp.data;
};

export const useGetVendorUsers = ({
	customerId,
	enabled = true,
}: { customerId?: number; enabled?: boolean } = {}) => {
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	const queryKey = useMemo(
		() => getVendorUsersQueryKey(customerId),
		[customerId],
	);
	const route = useMemo(() => {
		return isInternal
			? apiRoutes.INTERNALGETVENDORS.replace(
					":customerId",
					customerId?.toString() ?? "",
				)
			: apiRoutes.GETVENDORS;
	}, [isInternal, customerId]);
	const { data, isLoading, isError, isFetching } = useQuery({
		queryKey,
		queryFn: ({ signal }) => getVendorUsers(route, signal),
		enabled,
	});

	const refreshUsers = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey });
	}, [queryClient, queryKey]);

	return { users: data ?? [], isLoading, isError, isFetching, refreshUsers };
};
